import { Injectable } from "@angular/core";
import { Http, Headers, Response, RequestOptions } from "@angular/http";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import "rxjs/add/operator/map";
import { vehicleKmRecord, Action } from "../_models/vehicleKmRecord";
import { AuthenticationService } from "./authentication.service";
import { EnvironmentService } from "./index";
import { Customer } from "app/_models/customer";

@Injectable()
export class MonitorKmService {
  private baseUrl: string;

  constructor(
    private http: Http,
    private authenticationService: AuthenticationService,
    private environmentService: EnvironmentService
  ) {
    this.baseUrl = environment.production 
      ? this.environmentService.getBaseURL() + "/new-api"
      : "http://localhost:3004/new-api";
  }

  // Observable-based methods
  getAllKmRecords(dealerId: string): Observable<vehicleKmRecord[]> {
    let kmRecords$ = this.http
      .get(`${this.baseUrl}/vehicle-km-records/dealer/${dealerId}`, this.makeHeaders())
      .map(mapKmRecords)
      .catch(handleError);
    return kmRecords$;
  }

  getKmRecord(id: string): Observable<vehicleKmRecord> {
    let kmRecord$ = this.http
      .get(`${this.baseUrl}/vehicle-km-records/${id}`, this.makeHeaders())
      .map(mapKmRecord);
    return kmRecord$;
  }

  createKmRecord(kmRecord: vehicleKmRecord): Observable<vehicleKmRecord> {
    let newKmRecord = this.http
      .post(
        `${this.baseUrl}/vehicle-km-records`,
        JSON.stringify(kmRecord),
        this.makeHeaders()
      )
      .map(mapKmRecord);
    return newKmRecord;
  }

  updateKmRecord(kmRecord: vehicleKmRecord): Observable<Response> {
    return this.http.put(
      `${this.baseUrl}/vehicle-km-records/${kmRecord._id}`,
      JSON.stringify(kmRecord),
      this.makeHeaders()
    );
  }

  updateAction(recordId: string, action: Action): Observable<Response> {
    return this.http.patch(
      `${this.baseUrl}/vehicle-km-records/${recordId}/action`,
      JSON.stringify(action),
      this.makeHeaders()
    );
  }

  deleteKmRecord(id: string): Observable<Response> {
    return this.http
      .delete(`${this.baseUrl}/vehicle-km-records/${id}`, this.makeHeaders())
      .map((res: Response) => res.json())
      .catch((error: any) =>
        Observable.throw(error.json().error || "Server error")
      );
  }

  // Promise-based methods
  getAll(dealerId: string): Promise<vehicleKmRecord[]> {
    return this.getAllKmRecords(dealerId).toPromise();
  }

  get(id: string): Promise<vehicleKmRecord> {
    return this.getKmRecord(id).toPromise();
  }

  create(kmRecord: vehicleKmRecord): Promise<vehicleKmRecord> {
    return this.createKmRecord(kmRecord).toPromise();
  }

  update(kmRecord: vehicleKmRecord): Promise<Response> {
    return this.updateKmRecord(kmRecord).toPromise();
  }

  updateActionPromise(recordId: string, action: Action): Promise<Response> {
    return this.updateAction(recordId, action).toPromise();
  }

  delete(id: string): Promise<Response> {
    return this.deleteKmRecord(id).toPromise();
  }

  private makeHeaders(): RequestOptions {
    let headers = new Headers();
    headers.append("Authorization", this.authenticationService.getToken());
    headers.append("Content-Type", "application/json");
    headers.append("charset", "UTF-8");
    headers.append("Accept", "application/json");
    headers.append("Access-Control-Allow-Origin", "*");
    headers.append("Access-Control-Allow-Methods", "GET, POST, PUT, DELETE");
    headers.append("Access-Control-Allow-Headers", "Content-Type");
    headers.append("Access-Control-Allow-Credentials", "true");
    
    // Options for cross-domain requests
    let options = new RequestOptions({ 
      headers: headers
    });
    
    return options;
  }
}

function mapKmRecords(response: Response): vehicleKmRecord[] {
  const responseData = response.json();
  if (!responseData.success) {
    throw new Error('Failed to fetch km records');
  }
  
  const data = responseData.data;
  // If data is an array, map it directly
  if (Array.isArray(data)) {
    return data.map(toKmRecord);
  }
  // If data is a single object, wrap it in an array
  return [toKmRecord(data)];
}

function toKmRecord(r: any): vehicleKmRecord {
  const record = {
    _id: String(r._id),
    customer: r.customer,
    dealer: String(r.dealer),
    agreementStart: r.agreementStart,
    agreementEnd: r.agreementEnd,
    previousServiceDate: r.previousServiceDate,
    agreedKm: Number(r.agreedKm),
    actualKm: Number(r.actualKm),
    expectedKm: Number(r.expectedKm),
    kmDifference: Number(r.kmDifference),
    kmDifferencePercentage: Number(r.kmDifferencePercentage),
    kmDrivenPerYear: Number(r.kmDrivenPerYear),
    kmStatus: r.kmStatus,
    monitoredAt: r.monitoredAt,
    actions: r.actions || [],
    status: r.status || 'unresolved',
    createdAt: r.createdAt,
    updatedAt: r.updatedAt
  } as vehicleKmRecord;
  
  return record;
}

function mapKmRecord(response: Response): vehicleKmRecord {
  const responseData = response.json();
  if (!responseData.success) {
    throw new Error('Failed to fetch km record');
  }
  return toKmRecord(responseData.data);
}

function handleError(error: any) {
  let errorMsg =
    error.message ||
    `Yikes! There was was a problem with our hyperdrive device and we couldn't retrieve your data!`;
  console.error(errorMsg);
  return Observable.throw(errorMsg);
} 


// WEBPACK FOOTER //
// ./src/app/_services/monitorkm.service.ts